import { spawn } from 'child_process';
import { writeFile, unlink } from 'fs/promises';
import { join } from 'path';
import { tmpdir } from 'os';
import type {
  ComponentInfo,
  ScopeMode,
  WSStreamChunkMessage,
  WSStreamCompleteMessage,
  WSStreamToolUseMessage,
  WSSessionInfoMessage,
  AttachedFile,
  VisualEdit,
} from '@hotshot-it/shared';
import { createInterface } from 'readline';

interface UserMessage {
  type: 'user';
  message: {
    role: 'user';
    content: Array<{ type: 'text'; text: string }>;
  };
}

export interface AgentHandlerOptions {
  cwd: string;
  model?: string;
  systemPromptAdditions?: string;
  permissionMode?: 'acceptEdits' | 'bypassPermissions' | 'default' | 'plan';
  contextLevel?: 'narrow' | 'standard' | 'broad';
  broadcast?: (message: any) => void; // Broadcast function for streaming messages
}

interface StreamJsonMessage {
  type: 'system' | 'user' | 'assistant' | 'result';
  subtype?: 'init' | 'success' | 'error';
  message?: {
    role: string;
    id?: string;
    content: Array<{
      type: 'text' | 'tool_use' | 'tool_result';
      text?: string;
      name?: string;
      input?: any;
    }>;
  };
  session_id?: string;
  is_error?: boolean;
  result?: string;
}

export class AgentHandler {
  private options: AgentHandlerOptions;
  // componentId -> Claude CLI session ID
  private sessions = new Map<string, string>();
  // componentId -> message count (tracks conversation length)
  private messageCounts = new Map<string, number>();

  constructor(options: AgentHandlerOptions) {
    this.options = options;
  }

  /**
   * Generate a unique component ID for session tracking
   * Uses file path, component name, and element context for element-level granularity
   */
  private getComponentId(component: ComponentInfo): string {
    // Use element context for element-level session tracking
    // Different elements within the same component will have different contexts
    if (component.elementContext) {
      const ctx = component.elementContext;
      // Create unique signature from element properties
      const parts: string[] = [
        ctx.tagName,
        ctx.siblingIndex !== undefined ? String(ctx.siblingIndex) : 'x',
        ctx.text?.substring(0, 20) || '',
        ctx.classes?.slice(0, 2).join('-') || ''
      ];
      const signature = parts.join('|');

      // Hash the signature
      const hash = signature.split('').reduce((acc, char) => {
        return ((acc << 5) - acc) + char.charCodeAt(0);
      }, 0);

      return `${component.file}:${component.name}:${Math.abs(hash)}`;
    }
    return `${component.file}:${component.name}`;
  }

  /**
   * Build the prompt for Claude based on component info and user message
   */
  private buildPrompt(
    component: ComponentInfo,
    userMessage: string,
    scope: ScopeMode = 'component',
    screenshotPath?: string,
    userFilePaths?: string[]
  ): UserMessage {
    const lineRange = `${component.startLine}-${component.endLine}`;
    const propsInfo = component.props
      ? `\nCurrent props: ${JSON.stringify(component.props, null, 2)}`
      : '';

    const screenshotInfo = screenshotPath
      ? `\n\nComponent screenshot (current rendered state): @${screenshotPath}`
      : '';

    const userFilesInfo = userFilePaths && userFilePaths.length > 0
      ? `\n\nUser-provided files (currently in temp location):\n${userFilePaths.map(path => `- ${path}`).join('\n')}\n\nIMPORTANT: If you need to reference these files in the code, first move them to an appropriate location in the project using the Bash tool (e.g., cp command). Do not reference temp file paths in the code.`
      : '';

    // Build element context description for better Claude understanding
    let elementInfo = '';
    if (component.elementContext) {
      const ctx = component.elementContext;
      const parts: string[] = [];

      parts.push(`<${ctx.tagName}>`);

      if (ctx.text) {
        parts.push(`containing text: "${ctx.text}"`);
      }

      if (ctx.role) {
        parts.push(`with role: ${ctx.role}`);
      }

      if (ctx.classes && ctx.classes.length > 0) {
        parts.push(`classes: ${ctx.classes.join(', ')}`);
      }

      if (ctx.siblingIndex !== undefined && ctx.siblingIndex >= 0) {
        const position = ctx.siblingIndex === 0 ? '1st' :
                        ctx.siblingIndex === 1 ? '2nd' :
                        ctx.siblingIndex === 2 ? '3rd' :
                        `${ctx.siblingIndex + 1}th`;
        parts.push(`(${position} ${ctx.tagName} in parent)`);
      }

      elementInfo = `\n\nSelected element: ${parts.join(' ')}`;
    }

    let textContent: string;
    const contextLevel = this.options.contextLevel || 'standard';

    switch (scope) {
      case 'component':
        // If we have element context, make it element-focused
        if (component.elementContext) {
          // Adjust prompt based on context level
          let scopeGuidance: string;
          if (contextLevel === 'narrow') {
            scopeGuidance = 'IMPORTANT: Focus on changing ONLY this specific element. Do not modify other parts of the component unless absolutely necessary.';
          } else if (contextLevel === 'broad') {
            scopeGuidance = 'The user clicked on a specific element, but feel free to make related changes throughout the component or parent elements to achieve the desired result.';
          } else {
            // standard (default)
            scopeGuidance = 'Focus on this element, but feel free to adjust related elements if needed for visual consistency or to fully implement the requested change.';
          }

          textContent = `Edit the ${component.name} component:
${elementInfo}

File: @${component.file}
Component: ${component.name} (defined at line ${component.startLine})${propsInfo}${screenshotInfo}${userFilesInfo}

User request: "${userMessage}"

${scopeGuidance}`;
        } else {
          textContent = `Edit the ${component.name} component (lines ${lineRange}).

File: @${component.file}${propsInfo}${screenshotInfo}${userFilesInfo}

User request: "${userMessage}"

Make focused edits to the component.`;
        }
        break;

      case 'view':
        textContent = `Edit the file containing the ${component.name} component.

File: @${component.file}${propsInfo}${elementInfo}${screenshotInfo}${userFilesInfo}

User request: "${userMessage}"

Make the necessary changes across the view.`;
        break;

      case 'context':
        // For now, same as component. Later we can add parent detection
        return this.buildPrompt(component, userMessage, 'component', screenshotPath, userFilePaths);
    }

    return {
      type: 'user',
      message: {
        role: 'user',
        content: [{ type: 'text', text: textContent }],
      },
    };
  }

  /**
   * Save attached files to temp directory
   */
  private async saveAttachments(files: AttachedFile[]): Promise<string[]> {
    const savedPaths: string[] = [];

    for (const file of files) {
      try {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const filePath = join(tmpdir(), `hotshot-${timestamp}-${safeName}`);

        // Extract base64 data from data URL
        const base64Data = file.data.split(',')[1] || file.data;
        const buffer = Buffer.from(base64Data, 'base64');

        await writeFile(filePath, buffer);
        savedPaths.push(filePath);
        console.log('[Hotshot] Saved attachment:', filePath);
      } catch (error) {
        console.error('[Hotshot] Failed to save attachment:', file.name, error);
      }
    }

    return savedPaths;
  }

  /**
   * Clean up temporary attachment files
   */
  private async cleanupAttachments(filePaths: string[]): Promise<void> {
    for (const filePath of filePaths) {
      try {
        await unlink(filePath);
        console.log('[Hotshot] Cleaned up attachment:', filePath);
      } catch (error) {
        console.error('[Hotshot] Failed to cleanup attachment:', filePath, error);
      }
    }
  }

  /**
   * Handle a general chat message (not tied to a component)
   */
  async handleGeneralChat(
    userMessage: string,
    files?: AttachedFile[]
  ): Promise<void> {
    const generalSessionId = 'general:workspace';
    const existingSessionId = this.sessions.get(generalSessionId);
    const isNewSession = !existingSessionId;

    // Increment message count for general session
    const currentCount = this.messageCounts.get(generalSessionId) || 0;
    this.messageCounts.set(generalSessionId, currentCount + 1);

    // Save user-provided files
    let userFilePaths: string[] = [];
    if (files && files.length > 0) {
      userFilePaths = await this.saveAttachments(files);
    }

    try {
      const message = this.buildGeneralPrompt(userMessage, userFilePaths, isNewSession);

      console.log('[Hotshot] Starting Claude CLI general chat with:', {
        cwd: this.options.cwd,
        model: this.options.model,
        sessionId: existingSessionId || 'new',
        isNew: isNewSession,
        userFiles: userFilePaths.length,
      });

      const claudeSessionId = await this.runClaudeCLI(
        message,
        existingSessionId,
        generalSessionId
      );

      // Store the session ID from Claude CLI for future continuations
      if (claudeSessionId && !existingSessionId) {
        this.sessions.set(generalSessionId, claudeSessionId);
        console.log('[Hotshot] Stored Claude general session ID:', claudeSessionId);
      }

      // Send session info to frontend
      this.options.broadcast?.({
        type: 'session:info',
        sessionId: claudeSessionId,
        componentId: generalSessionId,
        messageCount: this.messageCounts.get(generalSessionId) || 1,
        isNew: isNewSession,
      });
    } catch (error) {
      console.error('[Hotshot] Agent error (general mode):', error);
      throw error;
    } finally {
      // Clean up temp files
      if (userFilePaths.length > 0) {
        await this.cleanupAttachments(userFilePaths);
      }
    }
  }

  /**
   * Handle a chat message for a component
   */
  async handleChat(
    component: ComponentInfo,
    userMessage: string,
    scope: ScopeMode = 'component',
    files?: AttachedFile[]
  ): Promise<void> {
    const componentId = this.getComponentId(component);
    const existingSessionId = this.sessions.get(componentId);
    const isNewSession = !existingSessionId;

    // Increment message count for this component
    const currentCount = this.messageCounts.get(componentId) || 0;
    this.messageCounts.set(componentId, currentCount + 1);

    // Save user-provided files
    let userFilePaths: string[] = [];
    if (files && files.length > 0) {
      userFilePaths = await this.saveAttachments(files);
    }

    // Save screenshot to temp file ONLY for new sessions (resumed sessions don't need it)
    let screenshotPath: string | undefined;
    if (component.screenshot && isNewSession) {
      const screenshotFile: AttachedFile = {
        name: 'component-screenshot.png',
        type: 'image/png',
        size: component.screenshot.length,
        data: component.screenshot,
      };
      const screenshotPaths = await this.saveAttachments([screenshotFile]);
      screenshotPath = screenshotPaths[0];
    }

    // Collect all temp files for cleanup
    const allTempFiles = [...userFilePaths];
    if (screenshotPath) {
      allTempFiles.push(screenshotPath);
    }

    try {
      // For new sessions, build full context prompt with screenshot
      // For resumed sessions, just send the user message with attachments (NO screenshot - it's stale)
      const message = isNewSession
        ? this.buildPrompt(component, userMessage, scope, screenshotPath, userFilePaths)
        : this.buildResumePrompt(userMessage, userFilePaths);

      console.log('[Hotshot] Starting Claude CLI query with:', {
        cwd: this.options.cwd,
        model: this.options.model,
        sessionId: existingSessionId || 'new',
        isNew: isNewSession,
        screenshot: !!screenshotPath,
        userFiles: userFilePaths.length,
      });

      console.log('[Hotshot] Message being sent:', JSON.stringify(message, null, 2));

      const claudeSessionId = await this.runClaudeCLI(
        message,
        existingSessionId,
        componentId
      );

      // Store the session ID from Claude CLI for future continuations
      if (claudeSessionId && !existingSessionId) {
        this.sessions.set(componentId, claudeSessionId);
        console.log('[Hotshot] Stored Claude session ID:', claudeSessionId);
      }
    } catch (error) {
      console.error('[Hotshot] Agent error:', error);
      throw error;
    } finally {
      // Clean up temp files
      if (allTempFiles.length > 0) {
        await this.cleanupAttachments(allTempFiles);
      }
    }
  }

  /**
   * Build prompt for resumed sessions
   * Note: Screenshots are NOT included in resumed sessions to avoid contradicting recent changes
   */
  private buildResumePrompt(
    userMessage: string,
    userFilePaths?: string[]
  ): UserMessage {
    const userFilesInfo = userFilePaths && userFilePaths.length > 0
      ? `\n\nUser-provided files (currently in temp location):\n${userFilePaths.map(path => `- ${path}`).join('\n')}\n\nIMPORTANT: If you need to reference these files in the code, first move them to an appropriate location in the project using the Bash tool (e.g., cp command). Do not reference temp file paths in the code.`
      : '';

    const textContent = `${userMessage}${userFilesInfo}`;

    return {
      type: 'user',
      message: {
        role: 'user',
        content: [{ type: 'text', text: textContent }],
      },
    };
  }

  /**
   * Build prompt for general mode (workspace-level questions)
   */
  private buildGeneralPrompt(
    userMessage: string,
    userFilePaths?: string[],
    isNewSession: boolean = false
  ): UserMessage {
    const userFilesInfo = userFilePaths && userFilePaths.length > 0
      ? `\n\nUser-provided files (currently in temp location):\n${userFilePaths.map(path => `- ${path}`).join('\n')}\n\nIMPORTANT: If you need to reference these files in the code, first move them to an appropriate location in the project using the Bash tool (e.g., cp command). Do not reference temp file paths in the code.`
      : '';

    let textContent: string;

    if (isNewSession) {
      // First message in general mode - provide context about workspace-level tasks
      textContent = `You are in General Workspace Chat mode. The user is asking a question or requesting help with a task that may involve:
- Architecture and design questions
- Multi-file changes or refactoring
- Adding new features across multiple components
- General coding questions about the codebase
- Analyzing patterns or finding code across the project

You have access to all your usual tools (Read, Edit, Glob, Grep, Bash) to explore the codebase, make changes, and assist the user.

User request: ${userMessage}${userFilesInfo}`;
    } else {
      // Resumed session - just send the message
      textContent = `${userMessage}${userFilesInfo}`;
    }

    return {
      type: 'user',
      message: {
        role: 'user',
        content: [{ type: 'text', text: textContent }],
      },
    };
  }

  /**
   * Run the Claude CLI and stream responses
   */
  private async runClaudeCLI(
    message: UserMessage,
    sessionId: string | undefined,
    componentId: string
  ): Promise<string | undefined> {
    return new Promise((resolve, reject) => {
      const args = [
        '-p',
        '--output-format', 'stream-json',
        '--input-format', 'stream-json',
        '--verbose',
        '--permission-mode', this.options.permissionMode || 'acceptEdits',
        '--allowedTools', 'Read,Edit,Grep,Glob,Bash',
      ];

      // Add model if specified
      if (this.options.model) {
        args.push('--model', this.options.model);
      }

      // Only resume if we have a valid Claude CLI session ID
      if (sessionId) {
        args.push('--resume', sessionId);
      }

      // Add system prompt
      const systemPrompt = `You are Hotshot - a visual React component editor.

${this.options.systemPromptAdditions || ''}

Guidelines:
- Make minimal, focused edits to components
- Preserve existing code structure and style
- Changes apply immediately via HMR
- User sees results instantly and can ask for refinements
- Respond conversationally about what you changed`;

      args.push('--append-system-prompt', systemPrompt);

      console.log('[Hotshot] Spawning claude CLI:', 'claude', args.join(' '));

      const child = spawn('claude', args, {
        cwd: this.options.cwd,
        stdio: ['pipe', 'pipe', 'pipe'], // Use stdin to send message
      });

      // Write message to stdin as JSONL
      const messageJson = JSON.stringify(message) + '\n';
      console.log('[Hotshot] Writing to Claude CLI stdin:', messageJson.trim());
      child.stdin.write(messageJson);
      child.stdin.end();
      console.log('[Hotshot] Closed stdin, waiting for Claude CLI output...');

      const filesChanged = new Set<string>();
      let errorMessage: string | undefined;
      let capturedSessionId: string | undefined;

      // Parse JSON lines from stdout
      const rl = createInterface({
        input: child.stdout,
        crlfDelay: Infinity,
      });

      rl.on('line', (line) => {
        console.log('[Hotshot] Claude CLI stdout (raw):', line);
        try {
          const message: StreamJsonMessage = JSON.parse(line);
          console.log('[Hotshot] Claude CLI message (parsed):', JSON.stringify(message, null, 2));

          // Capture session ID from any message that has it
          if (message.session_id && !capturedSessionId) {
            capturedSessionId = message.session_id;
            console.log('[Hotshot] Captured session ID from Claude CLI:', capturedSessionId);

            // Send session info to client when we get the ID
            const currentMessageCount = this.messageCounts.get(componentId) || 1;
            const sessionInfo: WSSessionInfoMessage = {
              type: 'session:info',
              sessionId: capturedSessionId,
              componentId,
              messageCount: currentMessageCount,
              isNew: !sessionId,
            };
            console.log('[Hotshot] Broadcasting session info:', JSON.stringify(sessionInfo, null, 2));
            this.options.broadcast?.(sessionInfo);
          }

          // Handle different message types
          if (message.type === 'assistant' && message.message) {
            console.log('[Hotshot] Processing assistant message with', message.message.content.length, 'content items');
            // Forward all content from Claude CLI to frontend
            for (const content of message.message.content) {
              if (content.type === 'text' && content.text) {
                const chunk: WSStreamChunkMessage = {
                  type: 'stream:chunk',
                  sessionId: capturedSessionId || sessionId || 'unknown',
                  chunk: content.text,
                  messageType: 'assistant',
                };
                console.log('[Hotshot] Broadcasting text chunk (length:', content.text.length, '):', content.text);
                this.options.broadcast?.(chunk);
              }

              // Forward tool use notifications to extension
              if (content.type === 'tool_use' && content.name) {
                const toolId = (content as any).id;

                const toolMsg: WSStreamToolUseMessage = {
                  type: 'stream:tool',
                  sessionId: capturedSessionId || sessionId || 'unknown',
                  tool: content.name,
                  toolId: toolId,
                  input: content.input as Record<string, unknown>,
                  status: 'start',
                };
                console.log('[Hotshot] Broadcasting tool use:', content.name, 'ID:', toolId, 'Input:', JSON.stringify(content.input));
                this.options.broadcast?.(toolMsg);

                // Track file changes from Edit tool
                if (content.name === 'Edit') {
                  const input = content.input as { file_path?: string };
                  if (input?.file_path) {
                    filesChanged.add(input.file_path);
                    console.log('[Hotshot] Tracking file change:', input.file_path);
                  }
                }
              }

              // Log tool results
              if (content.type === 'tool_result') {
                console.log('[Hotshot] Tool result for ID:', (content as any).tool_use_id);
              }
            }
          }

          // Log other message types
          if (message.type === 'system') {
            console.log('[Hotshot] System message:', message.subtype || 'no subtype');
          }

          if (message.type === 'user') {
            console.log('[Hotshot] User message (echoed back)');
          }

          // Handle result message
          if (message.type === 'result') {
            console.log('[Hotshot] Result message - is_error:', message.is_error, 'result:', message.result);
            if (message.is_error && message.result) {
              errorMessage = String(message.result);
            }

            // Send completion message
            const complete: WSStreamCompleteMessage = {
              type: 'stream:complete',
              sessionId: capturedSessionId || sessionId || 'unknown',
              filesChanged: Array.from(filesChanged),
            };
            console.log('[Hotshot] Broadcasting completion message:', JSON.stringify(complete, null, 2));
            this.options.broadcast?.(complete);
          }
        } catch (err) {
          console.error('[Hotshot] Failed to parse JSON line:', line, err);
        }
      });

      // Capture stderr for debugging
      let stderrData = '';
      child.stderr.on('data', (data) => {
        stderrData += data.toString();
        console.error('[Hotshot] Claude CLI stderr:', data.toString());
      });

      child.on('error', (err) => {
        console.error('[Hotshot] Child process error:', err);
        reject(new Error(`Failed to spawn claude CLI: ${err.message}`));
      });

      child.on('close', (code) => {
        console.log('[Hotshot] Claude CLI process closed with exit code:', code);
        if (errorMessage) {
          console.error('[Hotshot] Rejecting due to error message:', errorMessage);
          reject(new Error(errorMessage));
        } else if (code !== 0) {
          console.error('[Hotshot] Rejecting due to non-zero exit code:', code);
          reject(
            new Error(
              `Claude CLI exited with code ${code}.\nStderr: ${stderrData}`
            )
          );
        } else {
          console.log('[Hotshot] Claude CLI completed successfully. Session ID:', capturedSessionId || 'none');
          resolve(capturedSessionId);
        }
      });
    });
  }

  /**
   * Reset session for a component
   */
  resetSession(component: ComponentInfo): void {
    const componentId = this.getComponentId(component);
    this.sessions.delete(componentId);
    this.messageCounts.delete(componentId);
    console.log('[Hotshot] Session reset for component:', componentId);
  }

  /**
   * Clear all sessions
   */
  clearAllSessions(): void {
    this.sessions.clear();
    this.messageCounts.clear();
  }

  /**
   * Get session info for a component (for querying existing session state)
   */
  getSessionInfo(component: ComponentInfo): { sessionId: string; messageCount: number } | null {
    const componentId = this.getComponentId(component);
    const sessionId = this.sessions.get(componentId);
    if (!sessionId) return null;

    return {
      sessionId,
      messageCount: this.messageCounts.get(componentId) || 0,
    };
  }

  /**
   * Handle visual edits by translating them into Claude prompts
   */
  async handleVisualEdits(
    component: ComponentInfo,
    edits: VisualEdit[]
  ): Promise<void> {
    const componentId = this.getComponentId(component);
    const existingSessionId = this.sessions.get(componentId);

    // Increment message count for this component
    const currentCount = this.messageCounts.get(componentId) || 0;
    this.messageCounts.set(componentId, currentCount + 1);

    // Build prompt from visual edits
    const message = this.buildVisualEditPrompt(component, edits);

    console.log('[Hotshot] Starting Claude CLI query for visual edits:', {
      cwd: this.options.cwd,
      model: this.options.model,
      sessionId: existingSessionId || 'new',
      editsCount: edits.length,
    });

    console.log('[Hotshot] Message being sent:', JSON.stringify(message, null, 2));

    try {
      const claudeSessionId = await this.runClaudeCLI(
        message,
        existingSessionId,
        componentId
      );

      // Store the session ID from Claude CLI for future continuations
      if (claudeSessionId && !existingSessionId) {
        this.sessions.set(componentId, claudeSessionId);
        console.log('[Hotshot] Stored Claude session ID:', claudeSessionId);
      }
    } catch (error) {
      console.error('[Hotshot] Visual edit agent error:', error);
      throw error;
    }
  }

  /**
   * Build prompt for visual edits
   */
  private buildVisualEditPrompt(
    component: ComponentInfo,
    edits: VisualEdit[]
  ): UserMessage {
    // Describe each edit in natural language
    const editDescriptions = edits.map((edit, index) => {
      if (edit.type === 'text') {
        const ctx = edit.elementContext;
        const elementDesc = this.describeElementContext(ctx);
        return `${index + 1}. Change text in ${elementDesc}
   - Current text: "${edit.oldText}"
   - New text: "${edit.newText}"
   - Selector: ${edit.selector}`;
      } else if (edit.type === 'reorder') {
        const ctx = edit.elementContext;
        const elementDesc = this.describeElementContext(ctx);
        return `${index + 1}. Reorder children in ${elementDesc}
   - Parent: ${edit.parentSelector}
   - Original order: ${edit.oldOrder.join(', ')}
   - New order: ${edit.newOrder.join(', ')}`;
      } else if (edit.type === 'style') {
        const ctx = edit.elementContext;
        const elementDesc = this.describeElementContext(ctx);
        return `${index + 1}. Change style property in ${elementDesc}
   - Property: ${edit.property}
   - Current value: ${edit.oldValue}
   - New value: ${edit.newValue}
   - Selector: ${edit.selector}`;
      }
      return `${index + 1}. Unknown edit type`;
    }).join('\n\n');

    const textContent = `Apply these visual edits to the ${component.name} component:

File: @${component.file}
Component: ${component.name} (line ${component.startLine})

Visual Edits Made by User:
${editDescriptions}

IMPORTANT Instructions:
1. Use the Read tool to examine the current component code
2. For each edit, find the exact element described using the selector and element context
3. Make ONLY the changes specified - preserve all other code structure and styling
4. For text changes: Update the text content in the JSX
5. For reorder changes: Rearrange the child elements to match the new order
6. For style changes: Update the className, inline styles, or CSS as appropriate
7. Use the Edit tool to apply the changes
8. Respond conversationally about what you changed`;

    return {
      type: 'user',
      message: {
        role: 'user',
        content: [{ type: 'text', text: textContent }],
      },
    };
  }

  /**
   * Describe element context in natural language
   */
  private describeElementContext(ctx: any): string {
    const parts: string[] = [];
    parts.push(`<${ctx.tagName}>`);

    if (ctx.text) {
      parts.push(`with text "${ctx.text}"`);
    }

    if (ctx.classes && ctx.classes.length > 0) {
      parts.push(`(classes: ${ctx.classes.join(', ')})`);
    }

    if (ctx.siblingIndex !== undefined && ctx.siblingIndex >= 0) {
      const position = ctx.siblingIndex === 0 ? '1st' :
                      ctx.siblingIndex === 1 ? '2nd' :
                      ctx.siblingIndex === 2 ? '3rd' :
                      `${ctx.siblingIndex + 1}th`;
      parts.push(`- ${position} ${ctx.tagName} in parent`);
    }

    return parts.join(' ');
  }
}
